import { Bot, Check, Circle, Loader2, X, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { OptimizationQualityEval } from '../../../lib/optimization/qualityEval'
import type { QualityIssue } from '../../../lib/optimization/qualityEvalFlagging'
import {
  areAgentStepsTerminal,
  type AgentChatMessage,
  type OptimizationAgentUiState,
} from '../../../lib/optimization/optimizationAgentUi'
import type { SkillGapSummary } from '../../../lib/optimization/skillGapRecommendations'
import OptimizationFeedbackContent from './OptimizationFeedbackContent'

interface OptimizationAgentPanelProps {
  agentUi: OptimizationAgentUiState
  runId?: string
  qualityEval?: OptimizationQualityEval | null
  qualityIssues?: QualityIssue[]
  skillGapSummary?: SkillGapSummary | null
  liveSummaryProgrammaticPass?: boolean
  onMinimize: () => void
}

function MessageIcon({ kind }: { kind: AgentChatMessage['kind'] }) {
  switch (kind) {
    case 'active':
      return <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand-primary" aria-hidden />
    case 'done':
      return <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
    case 'error':
      return <Circle className="h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500" aria-hidden />
    case 'waiting':
      return <Circle className="h-3.5 w-3.5 shrink-0 text-text-muted/50" aria-hidden />
    default:
      return <Circle className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden />
  }
}

function HeaderStatusIcon({ status }: { status: OptimizationAgentUiState['headerStatus'] }) {
  switch (status) {
    case 'working':
      return <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
    case 'success':
      return <Check className="h-4 w-4" aria-hidden />
    case 'error':
      return <XCircle className="h-4 w-4" aria-hidden />
    default:
      return <Bot className="h-4 w-4" aria-hidden />
  }
}

function messageClass(kind: AgentChatMessage['kind']): string {
  switch (kind) {
    case 'active':
      return 'border-interactive/30 bg-interactive/5 text-text-primary font-medium'
    case 'done':
      return 'border-border-default bg-surface-muted/80 text-text-secondary'
    case 'error':
      return 'border-amber-300/60 bg-amber-50 text-amber-900'
    case 'waiting':
      return 'border-transparent bg-transparent text-text-muted'
    default:
      return 'border-border-default bg-surface-muted/60 text-text-secondary'
  }
}

export default function OptimizationAgentPanel({
  agentUi,
  runId,
  qualityEval,
  qualityIssues,
  skillGapSummary,
  liveSummaryProgrammaticPass,
  onMinimize,
}: OptimizationAgentPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const stepsTerminal = areAgentStepsTerminal(agentUi.messages)
  const showInlineFeedback =
    (agentUi.phase === 'complete' || agentUi.phase === 'failed') &&
    stepsTerminal &&
    qualityEval != null &&
    runId != null
  const [checklistCollapsed, setChecklistCollapsed] = useState(false)

  useEffect(() => {
    if (showInlineFeedback) {
      const timer = setTimeout(() => setChecklistCollapsed(true), 400)
      return () => clearTimeout(timer)
    }
    setChecklistCollapsed(false)
    return undefined
  }, [showInlineFeedback])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [agentUi.messages, agentUi.headline, agentUi.subline, showInlineFeedback, checklistCollapsed])

  if (!agentUi.showPanel) return null

  const headerTone =
    agentUi.headerStatus === 'success'
      ? 'bg-emerald-500/10 text-emerald-700'
      : agentUi.headerStatus === 'error'
        ? 'bg-amber-500/10 text-amber-800'
        : 'bg-brand-primary/10 text-brand-primary'

  return (
    <div
      className="optimization-agent-panel pointer-events-auto z-20 flex min-h-0 w-[min(22rem,40vw)] min-w-[14rem] shrink-0 flex-col self-stretch overflow-hidden border-l border-border-default bg-surface-default"
      role="region"
      aria-label="Optimization progress"
      aria-live="polite"
      data-testid="optimization-agent-panel"
      data-optimization-phase={agentUi.phase}
    >
      <header className="flex items-start gap-2 border-b border-border-default px-4 py-3">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${headerTone}`}
        >
          <HeaderStatusIcon status={agentUi.headerStatus} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Tailoring agent</p>
          <p className="truncate text-sm font-medium text-text-primary" data-testid="optimization-agent-headline">
            {agentUi.headline}
          </p>
          {agentUi.subline ? (
            <p
              className="mt-0.5 text-xs leading-snug text-text-secondary"
              data-testid="optimization-agent-subline"
            >
              {agentUi.subline}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          data-testid="optimization-agent-close"
          onClick={onMinimize}
          className="shrink-0 rounded-md p-1.5 text-text-muted hover:bg-surface-muted hover:text-text-primary"
          aria-label="Collapse tailoring agent to rail icon"
          title="Collapse to rail icon"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3">
        <div
          className={`optimization-agent-checklist space-y-2 overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out ${
            checklistCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
          }`}
          data-testid="optimization-agent-checklist"
          aria-hidden={checklistCollapsed}
        >
          {agentUi.messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs leading-relaxed ${messageClass(message.kind)}`}
              data-testid={`optimization-agent-msg-${message.id}`}
            >
              <MessageIcon kind={message.kind} />
              <span>{message.text}</span>
            </div>
          ))}
        </div>

        {showInlineFeedback && checklistCollapsed && (
          <div
            className="optimization-agent-feedback-inline"
            data-testid="optimization-agent-inline-feedback"
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Tailoring results</h3>
            <OptimizationFeedbackContent
              runId={runId}
              qualityEval={qualityEval}
              skillGapSummary={skillGapSummary}
              qualityIssues={qualityIssues}
              liveSummaryProgrammaticPass={liveSummaryProgrammaticPass}
              variant="inline"
            />
          </div>
        )}
      </div>
    </div>
  )
}
